import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { Loader2, ShieldCheck, Save, RotateCcw, Search, Check, X, Eye, Edit, Trash2 } from 'lucide-react';
import AdminLayout from '../../components/AdminLayout';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authCfg = () => {
  const token = localStorage.getItem('c24_token') || localStorage.getItem('c24_admin_token');
  return { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} };
};

const AdminPermissions = () => {
  const [catalog, setCatalog] = useState([]);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null); // {user, resolved, has_override, raw_override}
  const [perms, setPerms] = useState({}); // local edit buffer
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [c, u] = await Promise.all([
          axios.get(`${API}/admin/tabs`, authCfg()),
          axios.get(`${API}/admin/users`, authCfg()),
        ]);
        setCatalog(c.data.catalog || []);
        setUsers((u.data || []).filter((x) => x.role !== 'customer'));
      } catch (e) { console.warn(e); }
      setLoading(false);
    })();
  }, []);

  const loadUser = async (uid, opts = {}) => {
    setSelectedId(uid);
    setDetail(null);
    if (!opts.keepMsg) setMsg(null);
    try {
      const { data } = await axios.get(`${API}/admin/users/${uid}/permissions`, authCfg());
      setDetail(data);
      setPerms(JSON.parse(JSON.stringify(data.resolved || {})));
    } catch (e) { setMsg({ ok: false, text: e?.response?.data?.detail || 'Failed to load' }); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/users/${selectedId}/permissions`, { permissions: perms }, authCfg());
      setMsg({ ok: true, text: `Saved permissions for ${detail.user.email}` });
      loadUser(selectedId, { keepMsg: true });
    } catch (e) { setMsg({ ok: false, text: e?.response?.data?.detail || 'Save failed' }); }
    finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm('Reset this user to role defaults? Custom permissions will be removed.')) return;
    setSaving(true);
    try {
      await axios.delete(`${API}/admin/users/${selectedId}/permissions`, authCfg());
      setMsg({ ok: true, text: 'Reset to role defaults' });
      loadUser(selectedId, { keepMsg: true });
    } catch (e) { setMsg({ ok: false, text: e?.response?.data?.detail || 'Reset failed' }); }
    finally { setSaving(false); }
  };

  const togglePerm = (tabKey, action) => {
    setPerms((prev) => {
      const p = { ...(prev[tabKey] || { view: false, edit: false, delete: false }) };
      p[action] = !p[action];
      // Cascade: if edit/delete on, view must be on. If view off, clear all.
      if (action === 'edit' && p.edit) p.view = true;
      if (action === 'delete' && p.delete) p.view = true;
      if (action === 'view' && !p.view) { p.edit = false; p.delete = false; }
      return { ...prev, [tabKey]: p };
    });
  };

  const groups = useMemo(() => {
    const g = {};
    catalog.forEach((t) => { (g[t.group] = g[t.group] || []).push(t); });
    return g;
  }, [catalog]);

  const filtered = users.filter((u) => {
    if (roleFilter !== 'all' && u.role !== roleFilter) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q);
  });

  return (
    <AdminLayout title="Role & Permissions Manager">
      <div className="grid md:grid-cols-[320px_1fr] gap-4">
        {/* User list */}
        <div className="bg-white border border-neutral-200 rounded-2xl overflow-hidden self-start">
          <div className="p-3 border-b border-neutral-100 space-y-2">
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input data-testid="perm-search-input" value={search} onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-8 pr-3 py-2 rounded-lg border border-neutral-200 text-xs" placeholder="Search users…" />
            </div>
            <select data-testid="perm-role-filter" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}
              className="w-full px-2 py-1.5 rounded-lg border border-neutral-200 text-xs bg-white">
              <option value="all">All roles</option>
              <option value="admin">Admin</option>
              <option value="chief_accountant">Chief Accountant</option>
              <option value="franchise_admin">Franchise Admin</option>
              <option value="staff">Staff</option>
              <option value="cleaner">Cleaner</option>
            </select>
          </div>
          <div className="max-h-[68vh] overflow-y-auto">
            {loading && <div className="p-6 text-center"><Loader2 className="w-4 h-4 animate-spin mx-auto text-neutral-400" /></div>}
            {!loading && filtered.map((u) => (
              <button key={u.user_id} data-testid={`perm-user-${u.user_id}`} onClick={() => loadUser(u.user_id)}
                className={`w-full text-left px-3 py-2.5 border-b border-neutral-100 hover:bg-neutral-50 ${selectedId === u.user_id ? 'bg-[#F3E5F5]' : ''}`}>
                <p className="text-xs font-semibold truncate">{u.name}</p>
                <p className="text-[10px] text-neutral-500 truncate">{u.email}</p>
                <span className={`inline-block mt-1 text-[9px] font-bold px-1.5 py-0.5 rounded-full capitalize ${u.role === 'admin' ? 'bg-neutral-900 text-white' : u.role === 'chief_accountant' ? 'bg-indigo-100 text-indigo-700' : u.role === 'franchise_admin' ? 'bg-purple-100 text-purple-700' : u.role === 'cleaner' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'}`}>
                  {u.role}{u.staff_role ? ` · ${u.staff_role}` : ''}
                </span>
              </button>
            ))}
            {!loading && filtered.length === 0 && <p className="p-6 text-center text-xs text-neutral-400">No users found.</p>}
          </div>
        </div>

        {/* Permission grid */}
        <div>
          {msg && <div data-testid="perm-msg" className={`mb-3 px-3 py-2 rounded-lg text-xs ${msg.ok ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-800'}`}>{msg.text}</div>}

          {!selectedId && (
            <div className="bg-white border border-neutral-200 rounded-2xl p-10 text-center">
              <ShieldCheck className="w-12 h-12 mx-auto text-neutral-300" />
              <p className="mt-3 font-bold text-neutral-700">Pick a user to manage permissions</p>
              <p className="text-xs text-neutral-500 mt-1">Check <b>View</b> to grant sidebar access, <b>Edit</b> to allow modifications, <b>Delete</b> to allow removal.</p>
            </div>
          )}

          {selectedId && detail && (
            <div data-testid="perm-editor" className="bg-white border border-neutral-200 rounded-2xl overflow-hidden">
              <div className="px-5 py-4 border-b border-neutral-100 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                  <ShieldCheck className="w-5 h-5 text-purple-700" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sm">{detail.user.name}</p>
                  <p className="text-[11px] text-neutral-500">{detail.user.email} · {detail.user.role}{detail.user.staff_role ? ` · ${detail.user.staff_role}` : ''} · {detail.has_override ? 'Custom permissions' : 'Role defaults'}</p>
                </div>
                <button data-testid="perm-reset" onClick={reset} disabled={saving || !detail.has_override}
                  className="inline-flex items-center gap-1 bg-white border border-neutral-200 hover:border-red-300 text-red-600 text-xs font-semibold px-3 py-1.5 rounded-lg disabled:opacity-40">
                  <RotateCcw className="w-3.5 h-3.5" /> Reset to defaults
                </button>
                <button data-testid="perm-save" onClick={save} disabled={saving}
                  className="inline-flex items-center gap-1 bg-[#7B1FA2] hover:bg-[#6A1B9A] text-white text-xs font-semibold px-4 py-1.5 rounded-lg disabled:opacity-50">
                  <Save className="w-3.5 h-3.5" /> {saving ? 'Saving…' : 'Save permissions'}
                </button>
              </div>

              <div className="max-h-[68vh] overflow-y-auto">
                {Object.entries(groups).map(([group, tabs]) => (
                  <div key={group} className="border-b border-neutral-100 last:border-none">
                    <div className="px-4 py-2 bg-neutral-50 border-b border-neutral-100">
                      <p className="text-[11px] font-bold text-neutral-500 uppercase tracking-wider">{group}</p>
                    </div>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left">
                          <th className="px-4 py-2 text-[10px] font-bold text-neutral-500 uppercase">Tab / Tool</th>
                          <th className="px-3 py-2 text-[10px] font-bold text-neutral-500 uppercase w-16 text-center">View</th>
                          <th className="px-3 py-2 text-[10px] font-bold text-neutral-500 uppercase w-16 text-center">Edit</th>
                          <th className="px-3 py-2 text-[10px] font-bold text-neutral-500 uppercase w-16 text-center">Delete</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tabs.map((t) => {
                          const p = perms[t.key] || { view: false, edit: false, delete: false };
                          return (
                            <tr key={t.key} data-testid={`perm-row-${t.key}`} className="border-b border-neutral-100 hover:bg-neutral-50">
                              <td className="px-4 py-2 text-xs">
                                <p className="font-semibold">{t.label}</p>
                                <p className="text-[10px] text-neutral-400 font-mono">{t.key}</p>
                              </td>
                              <td className="px-3 py-2 text-center">
                                <ToggleCell data-testid={`perm-${t.key}-view`} on={p.view} icon={Eye} onClick={() => togglePerm(t.key, 'view')} />
                              </td>
                              <td className="px-3 py-2 text-center">
                                <ToggleCell data-testid={`perm-${t.key}-edit`} on={p.edit} icon={Edit} onClick={() => togglePerm(t.key, 'edit')} />
                              </td>
                              <td className="px-3 py-2 text-center">
                                <ToggleCell data-testid={`perm-${t.key}-delete`} on={p.delete} icon={Trash2} onClick={() => togglePerm(t.key, 'delete')} />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

const ToggleCell = ({ on, icon: Icon, onClick, ...rest }) => (
  <button {...rest} onClick={onClick}
    className={`w-8 h-8 rounded-md inline-flex items-center justify-center transition-colors ${on ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-neutral-100 text-neutral-300 hover:bg-neutral-200'}`}
    title={on ? 'Enabled — click to disable' : 'Disabled — click to enable'}>
    {on ? <Check className="w-4 h-4" /> : <X className="w-3.5 h-3.5" />}
  </button>
);

export default AdminPermissions;
